/*
 * bb-carousel.js — Beard & Brawn shared carousel custom element (<bb-carousel>)
 * Vanilla, no build step, dependency-free. Defer-safe, idempotent, theme-editor
 * lifecycle-safe, reduced-motion aware. Conventions mirror bb-motion.js:
 * passive listeners, rAF-gated scroll work, prefers-reduced-motion respect,
 * defensive null checks, everything torn down in disconnectedCallback.
 *
 * WHAT IT DOES
 *   Wraps a horizontal scroll-snap track and wires the optional chrome that
 *   sections render server-side in Liquid:
 *     - dot pagination     (aria-current synced to the active slide)
 *     - prev / next arrows (scroll one slide either way)
 *     - numeric counter    ("01 / 04" — current slide, padded)
 *     - progress rail fill  (scaleX proportional to current slide)
 *   Active slide is found by CENTER-DISTANCE math on a rAF-throttled passive
 *   scroll listener — NOT an IntersectionObserver threshold (the audit found a
 *   0.6 IO threshold freezes when a card can never reach 60% visibility on a
 *   narrow viewport). Center-distance always resolves a nearest card.
 *
 * KEY CONVENTION
 *   The component MANAGES existing light-DOM chrome; it does not generate it
 *   when the section already rendered it. Dots are only auto-generated as a
 *   fallback (data-generate-dots) when no server dots exist.
 *
 * SELF-DISABLING
 *   When the track is not actually scrollable (scrollWidth <= clientWidth — e.g.
 *   feature-carousel collapses to a static grid >=990px), the track is made
 *   non-focusable (tabIndex = -1) and arrow keys / dot scroll still work but the
 *   Tab stop is removed. A ResizeObserver re-evaluates on layout change.
 *
 * See the usage contract at the bottom of this file.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('bb-carousel')) return;
  if (!('customElements' in window)) return;

  var REDUCE = false;
  try {
    REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    REDUCE = false;
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function slice(list) {
    return Array.prototype.slice.call(list || []);
  }

  var BBCarousel = function () {
    return Reflect.construct(HTMLElement, [], BBCarousel);
  };
  BBCarousel.prototype = Object.create(HTMLElement.prototype);
  BBCarousel.prototype.constructor = BBCarousel;

  // -- Lifecycle ----------------------------------------------------------
  BBCarousel.prototype.connectedCallback = function () {
    if (this.__bbInit) return; // guard double-init (editor can re-attach)
    this.__bbInit = true;

    var trackSel = this.getAttribute('data-track') || '[data-bb-track]';
    this.track = this.querySelector(trackSel);
    if (!this.track) {
      this.__bbInit = false;
      return;
    }

    var slideSel = this.getAttribute('data-slide') || '[data-bb-slide]';
    this.slides = slice(this.track.querySelectorAll(slideSel));
    if (!this.slides.length) {
      this.__bbInit = false;
      return;
    }

    // Optional chrome — all resolved from the element's light DOM.
    this.dots = slice(this.querySelectorAll(this.getAttribute('data-dot') || '[data-bb-dot]'));
    this.prevBtn = this.querySelector(this.getAttribute('data-prev') || '[data-bb-prev]');
    this.nextBtn = this.querySelector(this.getAttribute('data-next') || '[data-bb-next]');
    this.counterEl = this.querySelector(this.getAttribute('data-counter') || '[data-bb-counter]');
    this.fillEl = this.querySelector(this.getAttribute('data-fill') || '[data-bb-fill]');

    // Fallback: generate dots only if asked AND none were rendered server-side.
    if (!this.dots.length && this.hasAttribute('data-generate-dots')) {
      this._buildDots();
    }

    this.active = -1;
    this._raf = 0;
    this._bound = {
      scroll: this._onScroll.bind(this),
      key: this._onKey.bind(this),
      prev: this._onPrev.bind(this),
      next: this._onNext.bind(this),
      resize: this._onResize.bind(this)
    };

    // ARIA on the dots container (role=group + label). NEVER tablist/tab.
    var dotsWrap = this.dots.length ? this.dots[0].parentElement : null;
    if (dotsWrap && !dotsWrap.getAttribute('role')) {
      dotsWrap.setAttribute('role', 'group');
      dotsWrap.setAttribute(
        'aria-label',
        this.getAttribute('data-dots-label') || 'Carousel pagination'
      );
    }

    // Wire dots.
    var self = this;
    this._dotHandlers = [];
    this.dots.forEach(function (dot, i) {
      // Normalise server-rendered dots to plain buttons (strip tab/tablist misuse).
      if (dot.getAttribute('role') === 'tab') dot.removeAttribute('role');
      if (!dot.getAttribute('aria-label')) {
        dot.setAttribute('aria-label', 'Go to item ' + (i + 1));
      }
      var h = function () { self.scrollToIndex(i); };
      dot.addEventListener('click', h);
      self._dotHandlers.push(h);
    });

    if (this.prevBtn) this.prevBtn.addEventListener('click', this._bound.prev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._bound.next);

    // Passive, rAF-gated scroll sync (center-distance, not IO threshold).
    this.track.addEventListener('scroll', this._bound.scroll, { passive: true });
    // Keyboard on the track (Left/Right/Home/End) — preserved from product-ritual.
    this.track.addEventListener('keydown', this._bound.key);

    // Overflow-driven focusability. ResizeObserver keeps it accurate across the
    // grid<->carousel breakpoint without any window-level listener.
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(this._bound.resize);
      this._ro.observe(this.track);
    } else {
      window.addEventListener('resize', this._bound.resize, { passive: true });
      this._usedWindowResize = true;
    }

    this._syncFocusable();
    this._update(); // seed active slide + chrome
  };

  BBCarousel.prototype.disconnectedCallback = function () {
    if (!this.__bbInit) return;
    this.__bbInit = false;

    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
    if (this.track) {
      this.track.removeEventListener('scroll', this._bound.scroll, { passive: true });
      this.track.removeEventListener('keydown', this._bound.key);
    }
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this._bound.prev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this._bound.next);

    var self = this;
    if (this._dotHandlers) {
      this.dots.forEach(function (dot, i) {
        dot.removeEventListener('click', self._dotHandlers[i]);
      });
    }
    if (this._ro) { this._ro.disconnect(); this._ro = null; }
    if (this._usedWindowResize) {
      window.removeEventListener('resize', this._bound.resize, { passive: true });
    }

    // Remove any dots we generated ourselves (leave server-rendered ones intact).
    if (this._generatedDots && this._generatedDots.parentNode) {
      this._generatedDots.parentNode.removeChild(this._generatedDots);
    }
    this._generatedDots = null;
    this._dotHandlers = null;
  };

  // -- Scrollability ------------------------------------------------------
  BBCarousel.prototype.isScrollable = function () {
    if (!this.track) return false;
    // +2px tolerance for sub-pixel rounding.
    return this.track.scrollWidth - this.track.clientWidth > 2;
  };

  BBCarousel.prototype._syncFocusable = function () {
    if (!this.track) return;
    var scrollable = this.isScrollable();
    // Only a real Tab stop when scrolling actually does something.
    this.track.tabIndex = scrollable ? 0 : -1;
    this.setAttribute('data-scrollable', scrollable ? 'true' : 'false');
  };

  // -- Active-slide detection (center distance) ---------------------------
  BBCarousel.prototype._nearestIndex = function () {
    var track = this.track;
    var center = track.scrollLeft + track.clientWidth / 2;
    var best = 0, bestDist = Infinity;
    for (var i = 0; i < this.slides.length; i++) {
      var s = this.slides[i];
      var mid = s.offsetLeft + s.offsetWidth / 2;
      var dist = Math.abs(mid - center);
      if (dist < bestDist) { bestDist = dist; best = i; }
    }
    return best;
  };

  BBCarousel.prototype._onScroll = function () {
    if (this._raf) return;
    var self = this;
    this._raf = requestAnimationFrame(function () {
      self._raf = 0;
      self._update();
    });
  };

  BBCarousel.prototype._onResize = function () {
    this._syncFocusable();
    this._update();
  };

  BBCarousel.prototype._update = function () {
    var i = this._nearestIndex();
    if (i === this.active) return;
    this.active = i;
    this._paint(i);
    this.dispatchEvent(
      new CustomEvent('bb-carousel:change', { bubbles: true, detail: { index: i } })
    );
  };

  BBCarousel.prototype._paint = function (i) {
    // Dots — aria-current on the active one (no other state class assumptions).
    var activeClass = this.getAttribute('data-dot-active-class');
    for (var d = 0; d < this.dots.length; d++) {
      var on = d === i;
      if (on) this.dots[d].setAttribute('aria-current', 'true');
      else this.dots[d].removeAttribute('aria-current');
      if (activeClass) this.dots[d].classList.toggle(activeClass, on);
    }
    // Counter — "03" (padded). The section renders the "/ NN" total statically.
    if (this.counterEl) this.counterEl.textContent = pad2(i + 1);
    // Progress rail — proportional fill via scaleX (transform-origin: left).
    if (this.fillEl) {
      this.fillEl.style.transform = 'scaleX(' + ((i + 1) / this.slides.length) + ')';
    }
  };

  // -- Navigation ---------------------------------------------------------
  BBCarousel.prototype.scrollToIndex = function (i) {
    if (!this.slides.length) return;
    i = Math.max(0, Math.min(this.slides.length - 1, i));
    var s = this.slides[i];
    if (!s) return;
    var track = this.track;
    // Centre the target card in the track's viewport (works for both start- and
    // center-snap tracks; for start-snap the card simply lands flush-left when it
    // can't be centred, which the browser's snap corrects).
    var left = s.offsetLeft - (track.clientWidth - s.offsetWidth) / 2;
    if (left < 0) left = 0;
    var behavior = REDUCE ? 'auto' : 'smooth';
    try {
      track.scrollTo({ left: left, behavior: behavior });
    } catch (e) {
      track.scrollLeft = left; // older engines: no options object
    }
    // Optimistic paint so the chrome updates instantly, even before scroll fires.
    this.active = i;
    this._paint(i);
  };

  BBCarousel.prototype._onPrev = function () {
    this.scrollToIndex((this.active < 0 ? 0 : this.active) - 1);
  };
  BBCarousel.prototype._onNext = function () {
    this.scrollToIndex((this.active < 0 ? 0 : this.active) + 1);
  };

  BBCarousel.prototype._onKey = function (e) {
    var cur = this.active < 0 ? 0 : this.active;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      this.scrollToIndex(cur + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.scrollToIndex(cur - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.scrollToIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.scrollToIndex(this.slides.length - 1);
    }
  };

  // -- Fallback dot generation (only when asked + none server-rendered) ---
  BBCarousel.prototype._buildDots = function () {
    var wrap = document.createElement('div');
    wrap.className = this.getAttribute('data-generated-dots-class') || 'bb-carousel__dots';
    for (var i = 0; i < this.slides.length; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('data-bb-dot', '');
      b.setAttribute('aria-label', 'Go to item ' + (i + 1));
      wrap.appendChild(b);
    }
    this.appendChild(wrap);
    this._generatedDots = wrap;
    this.dots = slice(wrap.querySelectorAll('[data-bb-dot]'));
  };

  customElements.define('bb-carousel', BBCarousel);
})();

/* =========================================================================
 * USAGE CONTRACT  (for the section-migration agents)
 * =========================================================================
 * Element:  <bb-carousel> … </bb-carousel>
 *
 * Required light-DOM children (matched by attribute selectors, all overridable):
 *   - ONE track element    [data-bb-track]   (the scroll-snap container)
 *   - N slide elements     [data-bb-slide]   (direct scroll-snap children,
 *                                              queried within the track)
 *
 * Optional light-DOM children:
 *   - dot buttons          [data-bb-dot]     (server-rendered <button>s; the
 *                                              component syncs aria-current)
 *   - prev button          [data-bb-prev]
 *   - next button          [data-bb-next]
 *   - numeric counter       [data-bb-counter] (textContent set to "03" padded;
 *                                              render the "/ NN" total yourself)
 *   - progress rail fill    [data-bb-fill]    (style.transform = scaleX(p);
 *                                              set transform-origin:left in CSS)
 *
 * Attributes on <bb-carousel> (all optional):
 *   data-track="SEL"          override track selector      (default [data-bb-track])
 *   data-slide="SEL"          override slide selector      (default [data-bb-slide])
 *   data-dot="SEL"            override dot selector        (default [data-bb-dot])
 *   data-prev / data-next     override arrow selectors     (default [data-bb-prev/next])
 *   data-counter="SEL"        override counter selector    (default [data-bb-counter])
 *   data-fill="SEL"           override rail fill selector  (default [data-bb-fill])
 *   data-dots-label="…"       aria-label for the dots group (default "Carousel pagination")
 *   data-dot-active-class="x" ALSO toggle class `x` on the active dot (e.g. "is-active")
 *                             — use when your existing CSS keys off a class, not aria-current
 *   data-generate-dots        generate fallback dots IF none are server-rendered
 *   data-generated-dots-class default "bb-carousel__dots" for generated wrapper
 *
 * Behaviour:
 *   - Active slide = nearest card centre to track centre (rAF-throttled passive
 *     scroll). Dots get aria-current="true"; counter + rail fill update.
 *   - Dot click / arrow button / Left|Right|Up|Down|Home|End (track focused)
 *     scroll the target slide into view (smooth; instant under reduced-motion).
 *   - Track tabIndex is 0 only while scrollable; -1 otherwise. Re-evaluated on
 *     resize via ResizeObserver. Reflected as data-scrollable="true|false" on
 *     the host (style hooks welcome).
 *   - Fully torn down on disconnect (editor section re-render safe). No
 *     window/document listeners outlive the element (except the ResizeObserver
 *     fallback, which is removed on disconnect).
 *
 * Events:
 *   'bb-carousel:change'  bubbles, detail:{ index }  — fires on active change.
 *
 * ARIA guidance:
 *   Keep dots as plain <button>s. The component sets role="group" +
 *   aria-label on the dots' parent and aria-current on the active dot. Do NOT
 *   use role="tablist"/"tab" (the component strips a stray role="tab").
 *
 * Minimal example:
 *   <bb-carousel data-dots-label="Product features">
 *     <div class="fc-track" data-bb-track>
 *       <article class="fc-card" data-bb-slide>…</article>
 *       <article class="fc-card" data-bb-slide>…</article>
 *     </div>
 *     <div class="fc-dots">
 *       <button type="button" class="fc-dot" data-bb-dot></button>
 *       <button type="button" class="fc-dot" data-bb-dot></button>
 *     </div>
 *   </bb-carousel>
 *
 * Example with counter + rail + arrows (product-ritual mobile):
 *   <bb-carousel data-dot-active-class="is-active">
 *     <span data-bb-counter>01</span> / 04
 *     <span class="rail"><span data-bb-fill></span></span>
 *     <button data-bb-prev>…</button>
 *     <div class="track" data-bb-track>
 *       <article data-bb-slide>…</article> …
 *     </div>
 *     <button data-bb-next>…</button>
 *   </bb-carousel>
 * ========================================================================= */
